import api from './api';

export const productReportsService = {
  /**
   * Pobierz najbardziej rotujące produkty
   * @param {Object} params - Parametry: limit, days, location_id
   * @returns {Promise} Lista produktów
   */
  async getHighestRotationProducts(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.days) queryParams.append('days', params.days);
      if (params.location_id) queryParams.append('location_id', params.location_id);
      
      const response = await api.get(`reports/products/rotation/highest?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania najbardziej rotujących produktów:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania raportów rotacji');
    }
  },

  /**
   * Pobierz najmniej rotujące produkty
   * @param {Object} params - Parametry: limit, days, location_id
   * @returns {Promise} Lista produktów
   */
  async getLowestRotationProducts(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.days) queryParams.append('days', params.days);
      if (params.location_id) queryParams.append('location_id', params.location_id);
      
      const response = await api.get(`reports/products/rotation/lowest?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania najmniej rotujących produktów:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania raportów rotacji');
    }
  },

  /**
   * Pobierz bestsellery (top produkty)
   * @param {Object} params - Parametry: limit, days, metric, location_id
   * @returns {Promise} Lista bestsellerów
   */
  async getBestsellingProducts(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.limit) queryParams.append('limit', params.limit);
      if (params.days) queryParams.append('days', params.days);
      if (params.metric) queryParams.append('metric', params.metric);
      if (params.location_id) queryParams.append('location_id', params.location_id);
      
      const response = await api.get(`reports/products/bestsellers?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania bestsellerów:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania bestsellerów');
    }
  },

  /**
   * Pobierz prognozę sprzedaży
   * @param {Object} params - Parametry: product_id, location_id, forecast_days, analysis_days
   * @returns {Promise} Prognoza sprzedaży
   */
  async getSalesForecast(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.product_id) queryParams.append('product_id', params.product_id);
      if (params.location_id) queryParams.append('location_id', params.location_id);
      if (params.forecast_days) queryParams.append('forecast_days', params.forecast_days);
      if (params.analysis_days) queryParams.append('analysis_days', params.analysis_days);
      
      const response = await api.get(`reports/products/forecast?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania prognozy sprzedaży:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania prognozy sprzedaży');
    }
  },

  /**
   * Pobierz podsumowanie raportów produktowych
   * @param {Object} params - Parametry: days, location_id
   * @returns {Promise} Podsumowanie
   */
  async getProductsSummary(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.days) queryParams.append('days', params.days);
      if (params.location_id) queryParams.append('location_id', params.location_id);

      const response = await api.get(`reports/products/summary?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania podsumowania produktów:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania podsumowania');
    }
  },

  /**
   * Pobierz raport stanu magazynu
   * @param {Object} params - Parametry: location_id
   * @returns {Promise} Raport magazynu
   */
  async getWarehouseStockReport(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.location_id) queryParams.append('location_id', params.location_id);

      const response = await api.get(`reports/products/warehouse-stock?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania raportu magazynu:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania raportu magazynu');
    }
  },

  /**
   * Wyszukaj produkty z ilością sprzedaną w okresie
   * @param {Object} params - Parametry: search, date_from, date_to, days, location_id
   * @returns {Promise} Lista produktów z ilością sprzedaną
   */
  async getSoldProductsSearch(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.search) queryParams.append('search', params.search);
      if (params.date_from) queryParams.append('date_from', params.date_from);
      if (params.date_to) queryParams.append('date_to', params.date_to);
      if (params.days) queryParams.append('days', params.days);
      if (params.location_id) queryParams.append('location_id', params.location_id);

      const response = await api.get(`reports/products/sold-search?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Błąd wyszukiwania sprzedanych produktów:', error);
      throw new Error(error.response?.data?.message || 'Błąd wyszukiwania produktów');
    }
  },

  /**
   * Pobierz raport dziennej sprzedaży produktów
   * @param {Object} params - Parametry: date (YYYY-MM-DD), location_id
   * @returns {Promise} Lista sprzedanych produktów z marżą
   */
  async getDailySalesReport(params = {}) {
    try {
      const queryParams = new URLSearchParams();
      if (params.date) queryParams.append('date', params.date);
      if (params.location_id) queryParams.append('location_id', params.location_id);

      const response = await api.get(`reports/products/daily-sales?${queryParams}`);
      return response.data;
    } catch (error) {
      console.error('Błąd pobierania raportu dziennej sprzedaży:', error);
      throw new Error(error.response?.data?.message || 'Błąd pobierania raportu');
    }
  }
};
